/**
 * The dark bubble that explains a control, and the machinery that puts it
 * somewhere it can actually be read.
 *
 * WHY THIS IS A PORTAL AND NOT AN ABSOLUTELY POSITIONED SPAN, which is the
 * obvious way to write it and is wrong here. Every table in this app lives
 * inside `overflow-x-auto overflow-y-auto` (`Table.tsx`), and a scroll container
 * clips absolutely positioned children. A bubble on the last column's header
 * would have been cut in half by the right edge and a bubble anywhere would have
 * been cut by the top, which is the edge this one opens towards. So it renders
 * into `document.body` at fixed coordinates taken from the trigger, where
 * nothing can clip it.
 *
 * WHY IT OPENS ON HOVER *AND* FOCUS *AND* CLICK. Hover alone is a desktop mouse
 * feature: it strands keyboard users and is actively broken on touch, where the
 * first tap would open it and the same tap would be swallowed. Focus covers the
 * keyboard, and click covers touch and gives anybody a way to pin it open while
 * they read. Escape and scroll close it, because a bubble positioned from a rect
 * taken before a scroll is a bubble pointing at nothing.
 *
 * WHY THE CLICK IS WATCHED IN THE CAPTURE PHASE. The trigger is whatever the
 * caller wrapped, and it is usually a control with a job of its own. A link that
 * cannot navigate is a far worse defect than a bubble that will not pin, so this
 * never calls `preventDefault` or `stopPropagation` on the trigger's behalf, and
 * it listens on the way down so that a trigger which stops the click itself,
 * as `InfoHint`'s marker does to protect a sortable header, is still seen.
 *
 * WHY THE PLACEMENT AND THE OPEN STATE ARE PURE FUNCTIONS OUTSIDE THE COMPONENT.
 * The workspace ships no DOM test environment, so anything written inside a
 * component here is untestable by construction, the same reason `SystemBanner`
 * keeps its copy in a plain module. Which event opens the bubble and which side
 * of the trigger it lands on are the two things a tooltip gets wrong, so both
 * are plain functions that `Tooltip.test.tsx` can hold to account.
 */
import type { ReactNode, RefObject } from 'react'
import { cloneElement, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'

// ---------------------------------------------------------------------------
// Where the bubble goes
// ---------------------------------------------------------------------------

/**
 * Above or below by default. Left and right are asked for by name: the
 * collapsed rail opens its labels to the right, and the spam panel's hints
 * open to the left, away from the text they explain.
 */
export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

/** The trigger's box in viewport coordinates, which is all the placement needs. */
export interface TooltipAnchor {
  top: number
  bottom: number
  left: number
  width: number
}

export interface TooltipGeometry {
  /** Viewport coordinates for the bubble's own box. */
  top: number
  left: number
  placement: TooltipPlacement
  /** The caret's centre, in pixels from the bubble's own left edge. */
  caretLeft: number
  /** For a bubble beside its trigger, the caret's centre from the bubble's own top edge. */
  caretTop?: number
}

export interface PlaceTooltipInput {
  anchor: TooltipAnchor
  bubble: { width: number; height: number }
  viewport: { width: number; height: number }
  /** A side asked for by name. Left out, the bubble goes above unless below is better. */
  prefer?: TooltipPlacement
}

/** How long a pointer rests on the trigger before the bubble opens. Focus opens it at once. */
export const TOOLTIP_DELAY_MS = 300

/** The space the caret lives in, between the trigger and the bubble. */
const GAP = 8
/** Closest the bubble is allowed to get to the edge of the window. */
const EDGE = 8
/**
 * Closest the caret is allowed to get to a corner. The bubble is `rounded-lg`,
 * so a caret any nearer than this grows out of the curve and reads as a glitch
 * rather than as a pointer.
 */
const CARET_INSET = 14
/** Half the caret's width, since `caretLeft` is its centre and CSS wants its edge. */
const CARET_HALF = 4

/**
 * Above by default, because that is what the design draws, and the trigger is
 * usually the last thing the eye was on so the bubble should not cover what
 * comes after it.
 *
 * The flip is deliberately not "below whenever above is tight". If neither side
 * has room, which happens in a short window, flipping puts the bubble somewhere
 * just as clipped and moves it away from where the reader is looking. So it
 * flips only when below is genuinely the better of the two.
 */
export function placeTooltip({ anchor, bubble, viewport, prefer }: PlaceTooltipInput): TooltipGeometry {
  if (prefer === 'left' || prefer === 'right') return placeBeside({ anchor, bubble, viewport, prefer })
  const needed = bubble.height + GAP
  const roomAbove = anchor.top
  const roomBelow = viewport.height - anchor.bottom
  const placement: TooltipPlacement = roomAbove >= needed || roomAbove >= roomBelow ? 'top' : 'bottom'

  const top = placement === 'top' ? anchor.top - GAP - bubble.height : anchor.bottom + GAP

  const centre = anchor.left + anchor.width / 2
  // `Math.max` is applied last on purpose. In a window narrower than the bubble
  // the right-hand limit goes negative, and clamping low first would pin the
  // bubble to a negative coordinate and push it off the left of the screen.
  const left = Math.max(EDGE, Math.min(centre - bubble.width / 2, viewport.width - bubble.width - EDGE))

  const caretRoom = Math.max(bubble.width - CARET_INSET, CARET_INSET)
  const caretLeft = Math.min(Math.max(centre - left, CARET_INSET), caretRoom)

  return { top, left, placement, caretLeft }
}

/**
 * Beside the trigger, on the side asked for unless that side cannot hold it
 * and the other side can. Vertically it centres on the trigger and is held
 * inside the window, with the caret kept on the trigger and off the corners.
 */
function placeBeside({
  anchor,
  bubble,
  viewport,
  prefer,
}: PlaceTooltipInput & { prefer: 'left' | 'right' }): TooltipGeometry {
  const right = anchor.left + anchor.width
  const roomLeft = anchor.left - GAP - EDGE
  const roomRight = viewport.width - right - GAP - EDGE
  const fits = (side: 'left' | 'right'): boolean => (side === 'left' ? roomLeft : roomRight) >= bubble.width
  const other = prefer === 'left' ? 'right' : 'left'
  const placement = fits(prefer) || !fits(other) ? prefer : other

  const left = placement === 'left' ? anchor.left - GAP - bubble.width : right + GAP
  const middle = (anchor.top + anchor.bottom) / 2
  const top = Math.max(EDGE, Math.min(middle - bubble.height / 2, viewport.height - bubble.height - EDGE))

  // A short bubble has no room for the inset, so the caret is simply centred.
  const inset = Math.min(CARET_INSET, bubble.height / 2)
  const caretTop = Math.min(Math.max(middle - top, inset), Math.max(bubble.height - inset, inset))

  return { top, left, placement, caretLeft: 0, caretTop }
}

// ---------------------------------------------------------------------------
// When the bubble is open
// ---------------------------------------------------------------------------

export type TooltipEvent =
  | 'pointer-enter'
  | 'pointer-leave'
  | 'focus'
  | 'blur'
  | 'press'
  | 'escape'
  /** A scroll or a resize, either of which makes the stored rect a lie. */
  | 'dismiss'

export interface TooltipState {
  open: boolean
  /** Set by a click, and the only thing a hover-out or a blur will not close. */
  pinned: boolean
}

export const TOOLTIP_CLOSED: TooltipState = { open: false, pinned: false }

export function nextTooltipState(state: TooltipState, event: TooltipEvent): TooltipState {
  switch (event) {
    case 'pointer-enter':
    case 'focus':
      return state.open ? state : { open: true, pinned: state.pinned }
    case 'pointer-leave':
    case 'blur':
      return state.pinned ? state : TOOLTIP_CLOSED
    case 'press':
      return state.pinned ? TOOLTIP_CLOSED : { open: true, pinned: true }
    case 'escape':
    case 'dismiss':
      return TOOLTIP_CLOSED
  }
}

// ---------------------------------------------------------------------------
// The bubble itself
// ---------------------------------------------------------------------------

export interface TooltipBubbleProps {
  id: string
  /**
   * Null until the bubble has been measured. It is rendered hidden for that one
   * pass rather than guessed at, and the pass is never seen: the measurement
   * runs in a layout effect, so React flushes the placement before the browser
   * paints.
   */
  geometry: TooltipGeometry | null
  children: ReactNode
  /** A bold first line. With one, `children` is drawn as the supporting text under it. */
  heading?: ReactNode
  elementRef?: RefObject<HTMLDivElement>
  className?: string
}

/**
 * The painted half, separated from the portal so it can be rendered on its own.
 *
 * `surface-inverse` is the palette's tooltip fill and this is its first call
 * site. The ink that goes on it is not a choice: `tokens.test.ts` is the thing
 * that pins a readable pair on that surface, and the pair it pins is
 * `--c-text-on-accent`, which is `text-ink-on` here. So the dark bubble and the
 * light ink on it are both tokens, and there is no `dark:` utility in this file
 * and nothing to add if the fill is ever changed.
 */
/** Which edge the caret sits on: the one facing the trigger. */
function caretEdge(placement: TooltipPlacement | undefined): string {
  if (placement === 'bottom') return '-top-1'
  if (placement === 'left') return '-right-1'
  if (placement === 'right') return '-left-1'
  return '-bottom-1'
}

function caretStyle(geometry: TooltipGeometry | null): { left?: number; top?: number } | undefined {
  if (geometry === null) return undefined
  if (geometry.placement === 'left' || geometry.placement === 'right') {
    return { top: (geometry.caretTop ?? 0) - CARET_HALF }
  }
  return { left: geometry.caretLeft - CARET_HALF }
}

export function TooltipBubble({ id, geometry, children, heading, elementRef, className }: TooltipBubbleProps) {
  return (
    <div
      id={id}
      role="tooltip"
      ref={elementRef}
      style={geometry === null ? { top: 0, left: 0, visibility: 'hidden' } : { top: geometry.top, left: geometry.left }}
      className={cn(
        // Its own tier, above the popover tier, so a tooltip on a row inside a
        // select inside a drawer still shows.
        'fixed z-tooltip max-w-[320px] rounded-lg bg-surface-inverse px-3 py-2',
        'text-xs font-semibold normal-case tracking-normal text-ink-on shadow-lg',
        className,
      )}
    >
      {heading !== undefined && heading !== null ? (
        <>
          <span className="block">{heading}</span>
          {/* The supporting line is the same ink at three quarters, which on
              this dark bubble lands on gray-300, through the one token. */}
          <span className="block font-medium text-ink-on/75">{children}</span>
        </>
      ) : (
        children
      )}
      {/* A square turned on its corner, in the bubble's own fill, so there is no
          seam to line up. It sits half outside the edge the bubble opened from. */}
      <span
        aria-hidden="true"
        className={cn('absolute h-2 w-2 rotate-45 bg-surface-inverse', caretEdge(geometry?.placement))}
        style={caretStyle(geometry)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The component
// ---------------------------------------------------------------------------

/** The one prop the trigger is given, so a screen reader reads the bubble with it. */
interface TriggerProps {
  'aria-describedby'?: string
}

export interface TooltipProps {
  /** What the bubble says. Plain prose, one or two sentences. */
  content: ReactNode
  /** The control the bubble explains. Anything: a button, a link, a glyph. */
  children: ReactNode
  /** Classes for the wrapper around the trigger, not for the bubble. */
  className?: string
  /** A bold first line above `content`. */
  heading?: ReactNode
  /** Ask for a side. Left out, the bubble goes above, or below when above is worse. */
  placement?: TooltipPlacement
}

export function Tooltip({ content, children, className, heading, placement }: TooltipProps) {
  const [state, setState] = useState<TooltipState>(TOOLTIP_CLOSED)
  const [geometry, setGeometry] = useState<TooltipGeometry | null>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const send = (event: TooltipEvent): void => {
    setState((current) => nextTooltipState(current, event))
  }

  const cancelHover = (): void => {
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current)
    hoverTimer.current = null
  }

  // A pointer passing over a row of icons on its way somewhere else should not
  // light each one up, so hover waits. Focus does not: somebody who tabbed here
  // asked to be here.
  useEffect(() => cancelHover, [])

  useLayoutEffect(() => {
    if (!state.open) {
      setGeometry(null)
      return
    }
    const anchor = anchorRef.current?.getBoundingClientRect()
    const bubble = bubbleRef.current?.getBoundingClientRect()
    if (anchor === undefined || bubble === undefined) return
    setGeometry(
      placeTooltip({
        anchor,
        bubble: { width: bubble.width, height: bubble.height },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        prefer: placement,
      }),
    )
    // Only on the open, and deliberately not on `content`. A ReactNode is a new
    // object every render, so listing it would re-run this on every render and
    // the `setGeometry` inside would never settle.
  }, [state.open])

  useEffect(() => {
    if (!state.open) return undefined
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') send('escape')
    }
    // Capture, so a scroll inside the table itself closes it and not only a
    // scroll of the page. The rect this was positioned from is stale either way.
    const onScroll = (): void => send('dismiss')
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [state.open])

  // Left alone while the bubble is shut, so a trigger that carries its own
  // `aria-describedby` keeps it until there is something else to describe it.
  const trigger =
    state.open && isValidElement<TriggerProps>(children)
      ? cloneElement(children, { 'aria-describedby': id })
      : children

  return (
    <>
      <span
        ref={anchorRef}
        className={cn('inline-flex', className)}
        onMouseEnter={() => {
          cancelHover()
          hoverTimer.current = setTimeout(() => send('pointer-enter'), TOOLTIP_DELAY_MS)
        }}
        onMouseLeave={() => {
          cancelHover()
          send('pointer-leave')
        }}
        onFocus={() => {
          cancelHover()
          send('focus')
        }}
        onBlur={() => send('blur')}
        onClickCapture={() => send('press')}
      >
        {trigger}
      </span>
      {state.open
        ? createPortal(
            <TooltipBubble id={id} geometry={geometry} heading={heading} elementRef={bubbleRef}>
              {content}
            </TooltipBubble>,
            document.body,
          )
        : null}
    </>
  )
}
